import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { getLiveMatches, getCompletedMatches, getScheduledMatches, getTeams } from '@/lib/supabase';
import AppHeader from '@/components/AppHeader';
import type { Team } from '@/types';

interface MatchRow {
  id: string;
  matchNumber: number;
  status: string;
  teamA: Team | null;
  teamB: Team | null;
  tournamentId: string | null;
  createdAt: string;
}

interface ScorerDashboardProps {
  onSignOut: () => void;
}

export default function ScorerDashboard({ onSignOut }: ScorerDashboardProps) {
  const navigate = useNavigate();
  const { profile, isViewer, isScorer, isAdmin } = useAuth();
  const [liveMatches, setLiveMatches] = useState<MatchRow[]>([]);
  const [completedMatches, setCompletedMatches] = useState<MatchRow[]>([]);
  const [scheduledMatches, setScheduledMatches] = useState<MatchRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const role = isAdmin ? 'admin' : isScorer ? 'scorer' : isViewer ? 'viewer' : 'unknown';

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rawLiveMatches, rawCompletedMatches, rawTeams] = await Promise.all([
        getLiveMatches(),
        getCompletedMatches(),
        getTeams(),
      ]);

      const teams: Team[] = rawTeams.map((t: any) => ({
        id: t.id,
        name: t.name,
        shortName: t.short_name,
        color: t.color,
        players: [],
      }));

      const mapMatch = (m: any): MatchRow => ({
        id: m.id,
        matchNumber: m.match_number,
        status: m.status,
        teamA: teams.find(t => t.id === m.team_a_id) ?? null,
        teamB: teams.find(t => t.id === m.team_b_id) ?? null,
        tournamentId: m.tournament_id ?? null,
        createdAt: m.created_at,
      });

      const liveRows = rawLiveMatches.map(mapMatch);
      const completedRows = rawCompletedMatches.map(mapMatch);

      setLiveMatches(liveRows);
      setCompletedMatches(completedRows);

      // Fetch scheduled matches for scorer/admin (viewers don't see them)
      if (!isViewer) {
        try {
          const rawScheduled = await getScheduledMatches();
          setScheduledMatches(rawScheduled.map(mapMatch));
        } catch {
          setScheduledMatches([]);
        }
      } else {
        setScheduledMatches([]);
      }

      // Console logging
      const tournamentMatches = [...liveRows, ...completedRows].filter(m => m.tournamentId).length;
      const standaloneMatches = [...liveRows, ...completedRows].filter(m => !m.tournamentId).length;
      console.log(`[Dashboard] liveCount=${liveRows.length}, completedCount=${completedRows.length}, scheduledCount=${scheduledMatches.length}, role=${role}, tournamentMatches=${tournamentMatches}, standaloneMatches=${standaloneMatches}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [role, isViewer]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  const statusLabel = (status: string) => {
    switch (status) {
      case 'live': return 'LIVE';
      case 'set-won': return 'SET WON';
      case 'first-server': return 'SELECTING SERVER';
      case 'match-won': return 'COMPLETED';
      default: return status.toUpperCase();
    }
  };

  const getActionLabel = (status: string) => {
    if (status === 'match-won') return 'View';
    return isViewer ? 'View' : 'Resume';
  };

  const getMatchRoute = (matchId: string, _status: string) => {
    if (isViewer) return `/match/${matchId}/view`;
    return `/match/${matchId}/resume`;
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AppHeader
        onSignOut={onSignOut}
        title="Match Dashboard"
        navIcon="home"
        onNavigate={() => navigate('/scorer')}
      />

      <div className="flex-1 overflow-y-auto px-4 py-5 safe-area-bottom max-w-md mx-auto w-full">
        {/* Welcome */}
        {profile && (
          <div className="mb-5">
            <h2 className="text-base font-bold text-text-primary">
              Welcome, {profile.displayName}
            </h2>
            <p className="text-xs text-text-muted mt-0.5">
              {isViewer ? 'View matches and tournaments below.' : 'Select a match to resume scoring.'}
            </p>
          </div>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-1 gap-3 mb-5">
          <button
            onClick={() => navigate('/tournaments')}
            className="card flex items-center gap-3 hover:border-info/50 hover:bg-info/5 transition-colors active:scale-[0.99]"
          >
            <div className="w-9 h-9 rounded-full bg-info/15 flex items-center justify-center flex-shrink-0">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-info">
                <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
              </svg>
            </div>
            <div>
              <span className="block text-sm font-bold text-text-primary">Tournaments</span>
              <span className="block text-[11px] text-text-muted">Create matches from a tournament</span>
            </div>
          </button>
        </div>

        {error && (
          <div className="bg-error/15 border border-error/30 rounded-lg p-3 mb-4 flex items-center justify-between">
            <p className="text-error text-sm">{error}</p>
            <button onClick={loadDashboard} className="text-xs text-error underline ml-3">Retry</button>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <span className="w-8 h-8 border-2 border-text-muted border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* SCHEDULED MATCHES — scorer/admin only, viewers cannot see */}
            {!isViewer && scheduledMatches.length > 0 && (
              <section className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-bold text-text-muted uppercase tracking-wider">
                    SCHEDULED MATCHES
                  </h3>
                  <span className="text-xs font-bold text-info bg-info/15 px-2 py-0.5 rounded-full">
                    {scheduledMatches.length}
                  </span>
                </div>
                <div className="space-y-2">
                  {scheduledMatches.map(m => (
                    <div
                      key={m.id}
                      className="card w-full text-left flex items-center gap-3"
                    >
                      <span className="text-xs font-bold text-text-muted">#{m.matchNumber}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          {m.teamA && (
                            <span className="text-sm font-semibold" style={{ color: m.teamA.color }}>
                              {m.teamA.shortName}
                            </span>
                          )}
                          <span className="text-text-muted text-xs">vs</span>
                          {m.teamB && (
                            <span className="text-sm font-semibold" style={{ color: m.teamB.color }}>
                              {m.teamB.shortName}
                            </span>
                          )}
                        </div>
                        <p className="text-[10px] text-text-muted mt-0.5 capitalize">
                          {new Date(m.createdAt).toLocaleDateString()} · {m.status}
                        </p>
                      </div>
                      <span className="text-xs font-bold text-info bg-info/10 px-2 py-1 rounded">
                        Start
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* LIVE MATCHES */}
            <section className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-bold text-text-muted uppercase tracking-wider">
                  LIVE MATCHES
                </h3>
                <span className="text-xs font-bold text-warning bg-warning/15 px-2 py-0.5 rounded-full">
                  {liveMatches.length}
                </span>
              </div>

              {liveMatches.length === 0 ? (
                <div className="card text-center py-6 border-dashed">
                  <p className="text-text-muted text-sm">No live matches.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {liveMatches.map(m => (
                    <MatchCard
                      key={m.id}
                      match={m}
                      statusLabel={statusLabel(m.status)}
                      actionLabel={getActionLabel(m.status)}
                      onClick={() => navigate(getMatchRoute(m.id, m.status))}
                    />
                  ))}
                </div>
              )}
            </section>

            {/* COMPLETED MATCHES */}
            <section className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-bold text-text-muted uppercase tracking-wider">
                  COMPLETED MATCHES
                </h3>
                <span className="text-xs font-bold text-success bg-success/15 px-2 py-0.5 rounded-full">
                  {completedMatches.length}
                </span>
              </div>

              {completedMatches.length === 0 ? (
                <div className="card text-center py-6 border-dashed">
                  <p className="text-text-muted text-sm">No completed matches.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {completedMatches.map(m => (
                    <MatchCard
                      key={m.id}
                      match={m}
                      statusLabel={statusLabel(m.status)}
                      actionLabel="View"
                      onClick={() => navigate(getMatchRoute(m.id, m.status))}
                    />
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}

function MatchCard({
  match,
  statusLabel,
  actionLabel,
  onClick,
}: {
  match: MatchRow;
  statusLabel: string;
  actionLabel: string;
  onClick: () => void;
}) {
  const isLive = match.status === 'live' || match.status === 'set-won';
  const isCompleted = match.status === 'match-won';

  return (
    <button
      onClick={onClick}
      className="w-full card text-left hover:border-warning/50 transition-colors active:scale-[0.99] group"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-text-muted">MATCH #{match.matchNumber}</span>
          {match.tournamentId && (
            <span className="text-[10px] text-text-muted bg-surface-raised px-1.5 py-0.5 rounded">TOURNAMENT</span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {isLive && <span className="w-2 h-2 rounded-full bg-warning animate-pulse" />}
          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${
            isLive
              ? 'bg-warning/20 text-warning'
              : isCompleted
                ? 'bg-success/20 text-success'
                : 'bg-surface-raised text-text-muted'
          }`}>
            {statusLabel}
          </span>
        </div>
      </div>

      {match.teamA && match.teamB ? (
        <div className="flex items-center justify-between">
          <TeamChip team={match.teamA} />
          <span className="text-xs font-bold text-text-muted px-2">VS</span>
          <TeamChip team={match.teamB} align="right" />
        </div>
      ) : (
        <p className="text-sm text-text-muted text-center py-1">Teams loading...</p>
      )}

      <div className="flex items-center justify-end mt-3 gap-1 text-text-muted group-hover:text-text-primary transition-colors">
        <span className="text-xs font-semibold">{actionLabel}</span>
        <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M8 4l6 6-6 6" />
        </svg>
      </div>
    </button>
  );
}

function TeamChip({ team, align = 'left' }: { team: Team; align?: 'left' | 'right' }) {
  return (
    <div className={`flex items-center gap-2 flex-1 ${align === 'right' ? 'flex-row-reverse' : ''}`}>
      <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: team.color }} />
      <span className="text-sm font-bold truncate" style={{ color: team.color }}>
        {team.shortName}
      </span>
    </div>
  );
}
